import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from "react";
import { useRecentProducts, RecentProduct } from "@/hooks/useRecentProducts";
import { useScopedDraftKey } from "@/hooks/useScopedDraftKey";
import { removeLegacyDraftKey } from "@/lib/scopedDraftStorage";

export interface CartItem {
  product_id: string;
  product_name: string;
  product_sku?: string;
  product_image_url?: string;
  quantity: number;
  unit_price: number;
  original_price: number;
  discount_percentage: number;
  discount_value: number;
  notes?: string;
  price_table_id?: string;
  price_table_name?: string;
  // Dados de contexto do cliente
  days_since_purchase?: number | null;
  last_quantity?: number | null;
  last_price?: number | null;
  last_sale_number?: string | null;
  // Flag para itens adicionados offline
  addedOffline?: boolean;
}

interface SaleCartContextType {
  items: CartItem[];
  customerId: string | null;
  customerName: string | null;
  orderId: string | null;
  orderType: 'sale' | 'quotation' | null;
  addItem: (item: CartItem) => void;
  updateItem: (productId: string, updates: Partial<CartItem>) => void;
  removeItem: (productId: string) => void;
  clearCart: () => void;
  setCustomer: (id: string | null, name: string | null) => void;
  setOrder: (id: string | null, type: 'sale' | 'quotation' | null) => void;
  getItemByProductId: (productId: string) => CartItem | undefined;
  total: number;
  itemCount: number;
  recentProducts: RecentProduct[];
  getRecentProductIds: () => string[];
}

const SaleCartContext = createContext<SaleCartContextType | undefined>(undefined);

export function SaleCartProvider({ children }: { children: ReactNode }) {
  // RASCUNHOS-NAVEGADOR (I.176): o carrinho mora numa chave com empresa + usuário. O provider monta antes do login,
  // então o carrinho é lido quando a identidade fica pronta, e só é gravado depois dessa leitura.
  const CART_STORAGE_KEY = useScopedDraftKey("sale_cart_items");
  const CART_CUSTOMER_KEY = useScopedDraftKey("sale_cart_customer");
  // Estado (não ref): no ciclo em que a chave muda, a gravação ainda vê a chave antiga e não sobrescreve nada
  const [loadedKey, setLoadedKey] = useState<string | null>(null);

  const [items, setItems] = useState<CartItem[]>([]);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState<string | null>(null);

  const [orderId, setOrderId] = useState<string | null>(null);
  const [orderType, setOrderType] = useState<'sale' | 'quotation' | null>(null);

  const { recentProducts, addToRecent, getRecentProductIds } = useRecentProducts();

  // Chaves antigas (fixas) descartadas: dono desconhecido não migra
  useEffect(() => {
    removeLegacyDraftKey("sale_cart_items");
    removeLegacyDraftKey("sale_cart_customer");
  }, []);

  // Ler o carrinho da identidade atual (troca de usuário ou empresa zera e relê)
  useEffect(() => {
    let storedItems: CartItem[] = [];
    let storedCustomer: { id: string | null; name: string | null } = { id: null, name: null };
    try {
      if (CART_STORAGE_KEY) {
        const stored = localStorage.getItem(CART_STORAGE_KEY);
        if (stored) storedItems = JSON.parse(stored);
      }
      if (CART_CUSTOMER_KEY) {
        const stored = localStorage.getItem(CART_CUSTOMER_KEY);
        if (stored) storedCustomer = JSON.parse(stored);
      }
    } catch {
      /* rascunho ilegível — começa vazio */
    }
    setItems(storedItems);
    setCustomerId(storedCustomer.id ?? null);
    setCustomerName(storedCustomer.name ?? null);
    setLoadedKey(CART_STORAGE_KEY);
  }, [CART_STORAGE_KEY, CART_CUSTOMER_KEY]);

  // Persist items to localStorage
  useEffect(() => {
    if (!CART_STORAGE_KEY || loadedKey !== CART_STORAGE_KEY) return;
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.error("Erro ao salvar carrinho:", e);
    }
  }, [items, CART_STORAGE_KEY, loadedKey]);

  // Persist customer to localStorage
  useEffect(() => {
    if (!CART_CUSTOMER_KEY || loadedKey !== CART_STORAGE_KEY) return;
    try {
      if (customerId && customerName) {
        localStorage.setItem(CART_CUSTOMER_KEY, JSON.stringify({ id: customerId, name: customerName }));
      }
    } catch (e) {
      console.error("Erro ao salvar cliente:", e);
    }
  }, [customerId, customerName, CART_CUSTOMER_KEY, CART_STORAGE_KEY, loadedKey]);

  const addItem = useCallback((item: CartItem) => {
    // Check if offline and mark item
    const isOffline = typeof navigator !== "undefined" && !navigator.onLine;
    const itemToAdd = isOffline ? { ...item, addedOffline: true } : item;

    setItems(prev => {
      const existingIndex = prev.findIndex(i => i.product_id === itemToAdd.product_id);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], ...itemToAdd };
        return updated;
      }
      return [...prev, itemToAdd];
    });
    
    // Salvar no histórico de recentes
    addToRecent({
      product_id: item.product_id,
      product_name: item.product_name,
      product_sku: item.product_sku,
      product_image_url: item.product_image_url,
      last_price: item.unit_price,
      last_quantity: item.quantity,
      added_at: new Date().toISOString(),
    });
  }, [addToRecent]);

  const updateItem = useCallback((productId: string, updates: Partial<CartItem>) => {
    setItems(prev => 
      prev.map(item => 
        item.product_id === productId 
          ? { ...item, ...updates }
          : item
      )
    );
  }, []);

  const removeItem = useCallback((productId: string) => {
    setItems(prev => prev.filter(item => item.product_id !== productId));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setCustomerId(null);
    setCustomerName(null);
    setOrderId(null);
    setOrderType(null);
    if (CART_STORAGE_KEY) localStorage.removeItem(CART_STORAGE_KEY);
    if (CART_CUSTOMER_KEY) localStorage.removeItem(CART_CUSTOMER_KEY);
  }, [CART_STORAGE_KEY, CART_CUSTOMER_KEY]);

  const setCustomer = useCallback((id: string | null, name: string | null) => {
    setCustomerId(id);
    setCustomerName(name);
  }, []);

  const setOrder = useCallback((id: string | null, type: 'sale' | 'quotation' | null) => {
    setOrderId(id);
    setOrderType(type);
  }, []);

  const getItemByProductId = useCallback((productId: string) => {
    return items.find(i => i.product_id === productId);
  }, [items]);

  const total = items.reduce((sum, item) => {
    const subtotal = item.quantity * item.unit_price;
    // discount_value é por unidade - multiplicar pela quantidade
    const discount = item.discount_value 
      ? item.discount_value * item.quantity 
      : (subtotal * item.discount_percentage) / 100;
    return sum + (subtotal - discount);
  }, 0);

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <SaleCartContext.Provider
      value={{
        items,
        customerId,
        customerName,
        orderId,
        orderType,
        addItem,
        updateItem,
        removeItem,
        clearCart,
        setCustomer,
        setOrder,
        getItemByProductId,
        total,
        itemCount,
        recentProducts,
        getRecentProductIds,
      }}
    >
      {children}
    </SaleCartContext.Provider>
  );
}

export function useSaleCart() {
  const context = useContext(SaleCartContext);
  if (context === undefined) {
    throw new Error("useSaleCart must be used within a SaleCartProvider");
  }
  return context;
}
